import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Button,
  Menu,
  MenuItem,
  TextField,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

import { Vendor, VendorType } from "../types";
import NotificationService from "../components/notification/NotificationService";
import { updateVendorValue } from "../api";
import { useAppDispatch } from "../hooks";
import { setVendors } from "../features/appSlice";

interface VendorTableProps {
  vendors: Vendor[];
  vendorTypes: VendorType[];
  setSelectedVendor: (vendor: Vendor) => void;
  setIsNew: (isNew: boolean) => void;
  setOpenDialog: (open: boolean) => void;
  setMainVendor: (vendor: Vendor) => void;
  setShowSecondaryDialog: (show: boolean) => void;
  setOpenDelete: (open: boolean) => void;
  setSelectedIdToDelete: (id?: string) => void;
}

const VendorTable: React.FC<VendorTableProps> = ({
  vendors,
  setSelectedVendor,
  setIsNew,
  setOpenDialog,
  setMainVendor,
  setShowSecondaryDialog,
  setOpenDelete,
  setSelectedIdToDelete,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuVendor, setMenuVendor] = useState<Vendor | null>(null);
  const dispatch = useAppDispatch();

  const isDesktop = useMediaQuery("(min-width: 720px)");

  const handleEdit = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    setIsNew(false);
    setOpenDialog(true);
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    vendor: Vendor
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuVendor(vendor);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuVendor(null);
  };

  const copyVendor = (vendor: Vendor) => {
    navigator.clipboard
      .writeText(`http://kalyanam.dk/edit/${vendor.contact.id}`)
      .then(() =>
        NotificationService.showToast({
          text: "Link kopieret",
          severity: "success",
        })
      )
      .catch(() =>
        NotificationService.showToast({
          text: "Link kunne ikke kopieres",
          severity: "error",
        })
      );
  };

  const updatePaymentDate = (id: string, date: Dayjs) => {
    // Gem kun dato som YYYY-MM-DD
    const isoDate = date.format("YYYY-MM-DD");

    updateVendorValue(id, "paymentDate", isoDate)
      .then(() => {
        const newData = vendors.map((ven) =>
          ven.id === id ? { ...ven, paymentDate: isoDate as any } : ven
        );
        dispatch(setVendors(newData));
        NotificationService.showToast({
          text: "Betalingsdato opdateret",
          severity: "success",
        });
      })
      .catch(() =>
        NotificationService.showToast({
          text: "Kunne ikke opdatere betalingsdato",
          severity: "error",
        })
      );
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell>Navn</TableCell>
            {isDesktop && (
              <>
                <TableCell>Type</TableCell>
                <TableCell>Primær/Sekundær</TableCell>
                <TableCell>By</TableCell>
                <TableCell>Betalingsdato</TableCell>
              </>
            )}
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {vendors.map((v) => (
            <TableRow key={v.id}>
              <TableCell>
                <Avatar src={v.logo ?? v.type?.logo ?? ""}>
                  {(v.mainVendor ? v.mainVendor.name : v.name)[0]}
                </Avatar>
              </TableCell>
              <TableCell>{v.mainVendor ? v.mainVendor.name : v.name}</TableCell>
              {isDesktop && (
                <>
                  <TableCell>{v.type?.name}</TableCell>
                  <TableCell>{v.mainVendor ? "Sekundær" : "Primær"}</TableCell>
                  <TableCell>{v.located}</TableCell>
                  <TableCell>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        disabled={!!v.mainVendor}
                        inputFormat="DD/MM/YYYY"
                        value={v.paymentDate ? dayjs(v.paymentDate) : null}
                        onChange={(newValue: Dayjs | null) => {
                          if (!newValue || !v.id) return;
                          updatePaymentDate(v.id, newValue);
                        }}
                        renderInput={(params) => <TextField {...params} />}
                      />
                    </LocalizationProvider>
                  </TableCell>
                </>
              )}
              <TableCell>
                <Button
                  onClick={(e) => handleMenuClick(e, v)}
                  startIcon={<MenuIcon />}
                />
                <Menu
                  anchorEl={menuAnchorEl}
                  open={Boolean(menuAnchorEl && menuVendor?.id === v.id)}
                  onClose={handleMenuClose}
                >
                  <MenuItem
                    onClick={() => {
                      menuVendor && copyVendor(menuVendor);
                      handleMenuClose();
                    }}
                  >
                    Kopier redigeringslink
                  </MenuItem>
                  <MenuItem
                    disabled={!!menuVendor?.mainVendor}
                    onClick={() => {
                      if (menuVendor) {
                        setMainVendor(menuVendor);
                        setShowSecondaryDialog(true);
                      }
                      handleMenuClose();
                    }}
                  >
                    Tilknyt sekundær
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      if (menuVendor) handleEdit(menuVendor);
                      handleMenuClose();
                    }}
                  >
                    Rediger
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      if (menuVendor) setSelectedIdToDelete(menuVendor.id);
                      setOpenDelete(true);
                      handleMenuClose();
                    }}
                  >
                    Slet
                  </MenuItem>
                </Menu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default VendorTable;
